import type { FC } from 'react';
import React, { useCallback } from 'react';
import cn from 'classnames';
import { ChevronForwardCompactSIcon } from '@alfalab/icons-glyph/ChevronForwardCompactSIcon';

import { ICON_THEME } from '../../helpers/config';
import type { IMenuEntry } from '../../models/desktop';

import type { TTopRow } from './models';

const filterHidden = (item: IMenuEntry) => !item.hidden;

const filterShown = (item: IMenuEntry) => !filterHidden(item);

// eslint-disable-next-line react/display-name
export const TopRow: FC<TTopRow> = React.memo(
    ({
        DataLayer,
        Img,
        SegmentLink,
        dataTestId,
        handleClick,
        segmentId,
        showAll,
        styles,
        theme,
        topRow,
        topRowIconSrc,
        withTopRowArrow = true,
    }) => {
        const renderRow = useCallback(
            (filterFn: (item: IMenuEntry) => boolean) =>
                topRow.filter(filterFn).map(({ analytics, key, link, title }: IMenuEntry) => (
                    <DataLayer key={key} data={analytics} tag={null}>
                        <SegmentLink
                            key={key}
                            link={link}
                            selected={key === segmentId}
                            title={title}
                        />
                    </DataLayer>
                )),
            [segmentId, topRow, DataLayer, SegmentLink]
        );

        return (
            <div className={styles.topRowWrapper}>
                <div className={styles.shownTopRow}>{renderRow(filterHidden)}</div>
                {withTopRowArrow && (
                    <>
                        <div
                            className={cn(styles.hiddenTopRow, {
                                [styles.hiddenTopRowAnimation]: showAll,
                            })}
                            data-test-id={`${dataTestId}-hidden-top-row`}
                        >
                            {renderRow(filterShown)}
                        </div>
                        {
                            // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
                            <span
                                className={cn(styles.iconRightWrapper, {
                                    [styles.rotate]: showAll,
                                })}
                                data-test-id={`${dataTestId}-icon-right-wrapper`}
                                onClick={handleClick}
                            >
                                {topRowIconSrc ? (
                                    <Img height={12} src={topRowIconSrc} width={6} />
                                ) : (
                                    <ChevronForwardCompactSIcon fill={ICON_THEME[theme]} />
                                )}
                            </span>
                        }
                    </>
                )}
            </div>
        );
    }
);
